/**
 * ProfileTab.jsx — User profile
 *
 * Edit own name, phone, avatar (upload). Sign-in is handled by Google; there are no passwords.
 */
import { useState, useCallback, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../lib/api';
import { logAction } from '../../lib/actionLogger';
import { useToast } from '../layout/Toast';
import { ACCENT_PALETTE } from '../../lib/userColors';
import UserAvatar from '../shared/UserAvatar';

export default function ProfileTab() {
  const { profile, refreshProfile } = useAuth();
  const { toast } = useToast();

  // ── Profile form ────────────────────────────────────────────────────────
  const [fullName, setFullName] = useState(profile?.full_name || '');
  const [phone, setPhone] = useState(profile?.phone || '');
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || '');
  const [accentColor, setAccentColor] = useState(profile?.accent_color || '#13B5EA');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const initials = (profile?.full_name || profile?.email || '?')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  // ── Save profile ────────────────────────────────────────────────────────
  const handleSaveProfile = useCallback(async () => {
    if (!fullName.trim()) {
      toast('Name is required', 'error');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/profiles/${profile.id}`, {
        full_name: fullName.trim(),
        phone: phone.trim() || null,
        avatar_url: avatarUrl.trim() || null,
        accent_color: accentColor,
      });
      await refreshProfile();
      logAction({
        action: 'Update Profile',
        description: `Updated own profile (${fullName.trim()})`,
        details: { profileId: profile.id, name: fullName.trim(), phone: phone.trim() || null },
      });
      toast('Profile updated');
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  }, [fullName, phone, avatarUrl, accentColor, profile, refreshProfile, toast]);

  // ── Upload avatar ────────────────────────────────────────────────────────
  const handleAvatarUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast('Image must be under 5 MB', 'error');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/upload/avatar', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const { url } = await res.json();
      setAvatarUrl(url);
      await refreshProfile();
      toast('Avatar uploaded');
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [refreshProfile, toast]);

  const handleRemoveAvatar = useCallback(async () => {
    setSaving(true);
    try {
      await api.put(`/profiles/${profile.id}`, { avatar_url: null });
      setAvatarUrl('');
      await refreshProfile();
      toast('Avatar removed');
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  }, [profile, refreshProfile, toast]);

  const inputCls = 'w-full px-3 py-2 bg-surface border border-border rounded text-text-primary placeholder-text-dim text-sm focus:outline-none focus:border-accent';

  return (
    <div className="space-y-6">
      {/* Avatar + Identity */}
      <div className="flex items-start gap-4">
        <div className="relative group">
          <UserAvatar name={profile?.full_name || profile?.email} color={accentColor} avatarUrl={avatarUrl} size="xl" className="border-2 border-border" />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            title="Upload photo"
          >
            <span className="text-white text-lg">{uploading ? '⏳' : '📷'}</span>
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
        </div>
        <div className="flex-1 space-y-1">
          <div className="text-lg font-semibold text-text-primary">{profile?.full_name || '—'}</div>
          <div className="text-sm text-text-dim">{profile?.email}</div>
          <span className="inline-block text-2xs px-2 py-0.5 rounded bg-accent/15 text-accent border border-accent/30 uppercase tracking-wider">
            {profile?.role?.replace(/_/g, ' ')}
          </span>
          <div className="flex gap-2 pt-1">
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="text-2xs text-accent hover:underline">
              {avatarUrl ? 'Change photo' : 'Upload photo'}
            </button>
            {avatarUrl && (
              <button onClick={handleRemoveAvatar} className="text-2xs text-text-dim hover:text-red-accent">Remove</button>
            )}
          </div>
        </div>
      </div>

      {/* Accent Color — Dropdown */}
      <div className="border border-border/50 rounded p-4 bg-surface-2/50 space-y-3">
        <h4 className="text-sm font-semibold text-text-primary">Your Color</h4>
        <p className="text-2xs text-text-dim">This color identifies you across the platform — in crew lists, job assignments, and avatars.</p>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-border flex-shrink-0" style={{ backgroundColor: accentColor }} />
          <select
            value={accentColor}
            onChange={(e) => setAccentColor(e.target.value)}
            className="flex-1 px-3 py-2 bg-surface border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent"
          >
            {ACCENT_PALETTE.map((c) => (
              <option key={c.hex} value={c.hex}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Profile Fields */}
      <div className="border border-border/50 rounded p-4 bg-surface-2/50 space-y-3">
        <h4 className="text-sm font-semibold text-text-primary">Profile Information</h4>

        <div>
          <label className="block text-xs font-medium text-text-dim mb-1">Full Name</label>
          <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} className={inputCls} />
        </div>

        <div>
          <label className="block text-xs font-medium text-text-dim mb-1">Email</label>
          <input type="email" value={profile?.email || ''} disabled className={`${inputCls} opacity-60 cursor-not-allowed`} />
          <p className="text-2xs text-text-dim mt-1">Email cannot be changed. Contact admin if needed.</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-text-dim mb-1">Phone</label>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-0000" className={inputCls} />
        </div>

        <div className="flex justify-end pt-2">
          <button
            onClick={handleSaveProfile}
            disabled={saving}
            className="px-4 py-2 bg-accent text-bg rounded text-sm font-medium hover:bg-accent-hover disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </div>

    </div>
  );
}
